import { memo, useEffect, useMemo, useRef, useState } from 'react'

import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useSelector } from 'react-redux'

import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
} from '@chakra-ui/react'

import { useIncidentQuery } from '@/graphql/generated/hooks'
import type { IncidentQuery } from '@/graphql/generated/operations'
import { selectMe } from '@/redux/me/meSlice'
import { selectIncident, selectIsVideoPlaying } from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'

import { AiOperatorFeedback } from '../ai-operator-feedback'
import { IncidentLocationInfo } from '../components/IncidentLocationInfo'
import { Feed } from '../feed'
import { SOPIncidentDrawer } from '../sop-incident-drawer/components/SOPIncidentDrawer'
import { TraceLogDrawer } from '../trace-log-drawer'
import { MixpanelDataIProps } from '../types/types'
import { isAccessDeniedCode } from '../utils/helpers'
import { AccessDeniedContainer } from './AccessDeniedContainer'
import { IncidentActions } from './IncidentActions'
import { IncidentDrawerHeader } from './IncidentDrawerHeader'
import { IncidentMedia } from './IncidentMedia'

export interface IncidentDrawerIProps {
  isOpen: boolean
  onClose: () => void
}

export const IncidentDrawer = memo(
  ({ isOpen, onClose }: IncidentDrawerIProps) => {
    const incidentId = useSelector(selectIncident)?.id
    const me = useSelector(selectMe)
    const bodyRef = useRef<HTMLDivElement>()
    const hasTrackedDrawerView = useRef(false)
    const [isAccessDenied, setIsAccessDenied] = useState(false)
    const isVideoPlaying = useSelector(selectIsVideoPlaying)
    const { traceLogs: isTraceLogsEnabled } = useFeatureFlags()

    // Cache data when video is playing to prevent UI from disappearing
    const [cachedData, setCachedData] = useState<IncidentQuery | null>(null)

    const {
      data,
      loading: isIncidentsLoading,
      refetch: refetchFeedItems,
      error,
    } = useIncidentQuery({
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      // Use cached data while video is playing - no polling during playback
      pollInterval: isAccessDenied || isVideoPlaying ? 0 : 3000,
      variables: {
        id: incidentId ?? '',
      },
    })

    useEffect(() => {
      if (data && !isVideoPlaying) {
        setCachedData(data)
      }
    }, [data, isVideoPlaying])

    // Reset cached data when switching to a different incident to prevent showing stale data
    // This ensures we don't display the old snapshot when the incident changes.
    useEffect(() => {
      if (incidentId) {
        setCachedData(null)
      }
    }, [incidentId])

    // Use cached data when video is playing, otherwise use fresh data
    const actualData = isVideoPlaying && cachedData ? cachedData : data

    const isLoading = !actualData || isIncidentsLoading
    const incident: IncidentQuery['incident'] = actualData?.incident
    const device = incident?.devices?.[0]
    const isArchived = incident?.facility?.archived
    const shouldShowIncidentActions = !(isLoading || isArchived)

    const scrollToTop = () => {
      bodyRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }

    const mixpanelData: MixpanelDataIProps = useMemo(() => {
      if (data) {
        return {
          incident_id: incidentId,
          incident_source: device?.type,
          incident_type: incident?.type,
          incident_status: incident?.status,
          device_id: device?.id,
          device_type: device?.type,
          facility_id: incident?.facility?.id,
          facility_address: incident?.facility?.address,
          floor_id: incident?.floor?.id,
          floor_name: incident?.floor?.name,
        }
      }
    }, [incidentId, data, incident?.type, device])

    useEffect(() => {
      if (!isOpen || !data || hasTrackedDrawerView.current) {
        return
      }

      hasTrackedDrawerView.current = true
      const date = new Date().toUTCString()
      mixpanel.track('Active Incident View, Drawer View', {
        user_id: me?.id,
        incident_id: incident?.id,
        event_at: date,
        incident_source: device?.type,
        incident_type: incident?.type,
        // NOTE! There's already an incident_source entry, but it's pointed to Device's Type. Ideally we'd have
        // that as this property's name, but I'm not sure that we should switch now as it'd muddy the historical data
        creation_method: incident?.source,
        device_id: device?.id,
        device_type: device?.type,
        facility_id: incident?.facility?.id,
        // Clips can come in after drawer open, but we're only looking to see
        has_clip_on_open: !!incident?.video?.url,
        seconds_since_created: incident?.createdAt
          ? Math.floor((Date.now() - Date.parse(incident.createdAt)) / 1000)
          : undefined,
      })
    }, [isOpen, incidentId, data, incident?.type, device])

    // Check if Access is denied for incident query
    useEffect(() => {
      if (error) {
        const isAccessDenied = isAccessDeniedCode(error)
        setIsAccessDenied(isAccessDenied)
      }
    }, [error])

    return (
      <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='lg'>
        <DrawerOverlay />
        <DrawerContent boxShadow='xl'>
          <SOPIncidentDrawer facilityId={incident?.facility?.id} />
          {isTraceLogsEnabled && <TraceLogDrawer incident={incident} />}
          <DrawerHeader
            bgColor='#ffffff'
            borderBottom='1px solid #D5DCE4'
            p={0}
            pr='24px'
          >
            <IncidentDrawerHeader
              incident={incident}
              isLoading={isLoading}
              onClose={onClose}
            />
          </DrawerHeader>
          {isAccessDenied ? (
            <AccessDeniedContainer />
          ) : (
            <DrawerBody bgColor='#ffffff' p={0} ref={bodyRef}>
              <AiOperatorFeedback incident={incident} />

              <IncidentMedia
                incident={incident}
                isLoading={isLoading}
                mixpanelData={mixpanelData}
                scrollToTop={scrollToTop}
              />

              <Box marginTop={4} marginX={4}>
                <IncidentLocationInfo
                  incident={incident}
                  isLoading={isLoading}
                  onClose={onClose}
                />
              </Box>

              {shouldShowIncidentActions && (
                <IncidentActions
                  incident={incident}
                  mixpanelData={mixpanelData}
                  refetchFeedItems={refetchFeedItems}
                />
              )}
              <Feed incident={incident} mixpanelData={mixpanelData} />
            </DrawerBody>
          )}
        </DrawerContent>
      </Drawer>
    )
  }
)
